<template>
  <v-card elevation="0" outlined class="d-flex flex-column h-100" min-height="350">
    <v-card-title class="py-1 white--text d-flex align-center justify-space-between" style="flex-wrap: nowrap;"
      :class="$vuetify.theme.dark ? 'black' : 'primary'">
      <div class="d-flex align-center" style="flex: 1 1 auto; min-width: 0;">
        <v-icon color="white" class="mr-2" medium>flare</v-icon>
        <span class="white--text body-font body-1 font-weight-bold text-uppercase text-ellipsis">{{
          $t("scoutingAnalysis.scoutingAnalyses") }}</span>
      </div>
      <v-btn class="pl-0 pr-0" text small :to="`/library`" style="flex-shrink: 0;">
        <span class="white--text text-decoration-underline font-weight-bold">{{ $t('library.goToLibrary') }}</span>
        <v-icon small class="ml-1" color="white">keyboard_arrow_right</v-icon>
      </v-btn>
    </v-card-title>
    <v-card-text class="d-flex flex-column pb-1 overflow-auto flex-grow-1" style="padding-top: 0px !important;">
      <template v-if="!loading">
        <v-data-table dense disable-items-per-page multi-sort :headers="headers" :items="analyses"
          :footer-props="{ itemsPerPageOptions: $vuetify.breakpoint.smAndDown ? [1] : [8] }" sort-by="tsUpdated"
          :sort-desc="true">
          <!--Player-->
          <template v-slot:item.playerId="{ item }">
            <router-link :to="`/player/${item.playerId}/scouting-analysis`">
              <span class="text-ellipsis body-1">{{ item.playerName }}</span>
            </router-link>
          </template>
          <!--Age-->
          <template v-slot:item.age="{ item }">
            <span class="body-1" v-if="item.age">{{ item.age }}</span>
            <span class="body-1" v-else>-</span>
          </template>
          <!--Position-->
          <template v-slot:item.fieldPositionId="{ item }">
            <v-chip v-if="item.fieldPositionId" small :color="getPositionChipColor(item.fieldPositionId)"
              class="monospace-font" text-color="white">
              {{ getPositionCodeText(item.fieldPositionId) }}
            </v-chip>
            <span v-else class="body-1">-</span>
          </template>
          <!--Team-->
          <template v-slot:item.teamId="{ item }">
            <router-link v-if="item.teamId" :to="`/team/${item.teamId}`" class="d-flex align-center justify-center">
              <img :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.teamId + '.png'"
                style="height: 20px; width: 20px;" @error="onImageLoadFailure($event)" />
            </router-link>
            <span v-else class="body-1">-</span>
          </template>
          <!--Global Rating-->
          <template v-slot:item.finalRating="{ item }">
            <div class="ma-0 py-1">
              <v-chip small :color="getRankingColor(item.finalRating)" class="monospace-font" text-color="white">{{
                item.finalRating }}</v-chip>
            </div>
          </template>
          <!--Recommendation-->
          <template v-slot:item.recommendationId="{ item }">
            <span class="text-ellipsis body-2 monospace-font">{{ item.recommendation.text }}</span>
          </template>
          <!--Scout-->
          <template v-slot:item.createdById="{ item }">
            <span class="text-ellipsis body-1">{{ item.createdByName }}</span>
          </template>
        </v-data-table>
      </template>

      <!-- Skeleton Loader -->
      <template v-else>
        <div class="mt-4 pl-6 pr-4 d-flex align-center justify-space-between">
          <v-skeleton-loader type="text" width="30%" class="ma-0"></v-skeleton-loader>
          <v-skeleton-loader type="text" width="15%" class="ma-0"></v-skeleton-loader>
        </div>
        <div class="px-6 py-4 flex-grow-1 d-flex flex-column justify-space-around">
          <v-skeleton-loader v-for="i in 4" :key="i" type="text" width="100%" class="my-2"></v-skeleton-loader>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import ResizeObserver from 'resize-observer-polyfill';
import * as _ from 'lodash';
import moment from 'moment';
import ScoutingAnalysisService from '../../../services/scoutingAnalysis.service';
import PlayerService from '../../../services/player/player.service';
import { colorScale, getPositionCode, getChipColorByPosition } from '../../../utils/functions';

export default {
  name: "LastScoutingAnalysesWidget",
  data() {
    return {
      loading: false,
      observer: null,
      analyses: [],
      headers: [
        {
          text: this.$t("general.player"),
          value: "playerId",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("playerScout.age"),
          value: "age",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("playerScout.position"),
          value: "fieldPositionId",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("general.team"),
          value: "teamId",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("scoutingAnalysis.globalRating"),
          value: "finalRating",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("documents.recommendation.label"),
          value: "recommendationId",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("scoutingAnalysis.scouter"),
          value: "createdById",
          align: "start",
          sortable: false,
        },
      ],
      recommendationItems: [{ id: 1, text: this.$t('documents.recommendation.1') },
      { id: 2, text: this.$t('documents.recommendation.2') },
      { id: 3, text: this.$t('documents.recommendation.3') },
      { id: 4, text: this.$t('documents.recommendation.4') }],
      visibilities: [{ id: 1, name: this.$t('collections.privacity.publicTitle'), icon: 'public', description: 'publicDescription' },
      { id: 3, name: this.$t('collections.privacity.supervisedTitle'), icon: 'visibility', description: 'supervisedDescription' },
      { id: 2, name: this.$t('collections.privacity.privateTitle'), icon: 'lock', description: 'privateDescription' }],
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
  },
  async created() {
    await this.loadScoutingAnalyses();
  },
  mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(this.$el);
  },
  methods: {
    onResize() {
      this.$emit('heightChange', this.$el.clientHeight)
    },
    async loadScoutingAnalyses() {
      this.loading = true;
      const analysesData = await ScoutingAnalysisService.getPlayerScoutingAnalysis();
      this.analyses = analysesData.data.map(a => {
        a.seeNotes = false;
        a.recommendation = this.recommendationItems.filter(r => a.recommendationId === r.id)[0];
        a.visibility = this.visibilities.filter(v => a.visibilityId === v.id)[0];
        a.updatedAt = moment(a.tsUpdated).format("DD/MM/YYYY HH:mm");
        return a;
      });

      // Enrich with player data (age and teamId)
      await this.enrichWithPlayerData();

      this.loading = false;
    },
    async enrichWithPlayerData() {
      // Get unique playerIds
      const uniquePlayerIds = [...new Set(this.analyses.map(a => a.playerId).filter(id => id))];

      // Fetch player data in parallel
      const playerPromises = uniquePlayerIds.map(playerId =>
        PlayerService.getPlayer(playerId).catch(err => {
          console.warn(`Failed to fetch player ${playerId}:`, err);
          return null;
        })
      );

      const playerResponses = await Promise.all(playerPromises);

      // Create a map of playerId -> player data
      const playerDataMap = {};
      playerResponses.forEach((response, index) => {
        if (response && response.data) {
          const playerId = uniquePlayerIds[index];
          playerDataMap[playerId] = response.data;
        }
      });

      // Enrich analyses with player data
      this.analyses = this.analyses.map(analysis => {
        const playerData = playerDataMap[analysis.playerId];
        if (playerData) {
          analysis.age = playerData.age || null;
          analysis.teamId = playerData.teamId || null;
        }
        return analysis;
      });
    },
    getRankingColor(value) {
      if (value) return colorScale(1, 10, value, 90);
    },
    formatTimestamp(date) {
      const formattedDate = moment(date).format("DD/MM/YYYY");
      return formattedDate;
    },
    getPositionCodeText(positionId) {
      if (!positionId) return '-';
      const fieldPositionCodeId = (this.currentUser && this.currentUser.fieldPositionCodeId) ? this.currentUser.fieldPositionCodeId : 1;
      return getPositionCode(fieldPositionCodeId, positionId);
    },
    getPositionChipColor(positionId) {
      if (!positionId) return 'grey';
      return getChipColorByPosition(positionId);
    },
    onImageLoadFailure(event) {
      event.target.src = 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png';
    },
  },
  destroyed() {
    delete this.observer;
  }
};
</script>